import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "password", label: "Password" },
  { id: "otp", label: "Email OTP" },
];

function PasswordTab({ onSuccess }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      onSuccess(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-password-form">
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="login-email-input" />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link to="/forgot-password" className="text-xs brand-orange font-semibold hover:underline" data-testid="forgot-password-link">Forgot?</Link>
        </div>
        <PasswordInput
          id="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="mt-1.5"
          testIdPrefix="login-password"
          data-testid="login-password-input"
        />
      </div>
      {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
      <Button type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold" data-testid="login-submit-button">
        {loading ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

function OtpTab({ onSuccess }) {
  const { requestLoginOtp, loginWithOtp } = useAuth();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  const startResendTimer = () => {
    setResendIn(30);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const send = async (e) => {
    if (e) e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await requestLoginOtp(email);
      setStep("code");
      startResendTimer();
      if (data && data.email_sent === false) {
        toast.warning("Code generated, but email couldn't be delivered. Contact your admin to verify the sender domain.");
      } else {
        toast.success("Sign-in code sent. Check your inbox.");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await loginWithOtp(email, code.trim());
      toast.success("Signed in!");
      onSuccess(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === "email") {
    return (
      <form onSubmit={send} className="mt-6 space-y-4" data-testid="login-otp-email-form">
        <div>
          <Label htmlFor="otp-email">Email</Label>
          <Input
            id="otp-email" type="email" required
            placeholder="you@restaurant.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5"
            data-testid="login-otp-email-input"
          />
          <p className="text-xs text-foreground/60 mt-2">
            We&rsquo;ll email you a 6-digit code. No password needed.
          </p>
        </div>
        {error && <div className="text-sm text-destructive" data-testid="login-otp-error">{error}</div>}
        <Button type="submit" disabled={loading || !email} className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold" data-testid="login-otp-send-button">
          {loading ? "Sending…" : "Send sign-in code"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="mt-6 space-y-4" data-testid="login-otp-code-form">
      <div>
        <Label htmlFor="otp-code">6-digit code</Label>
        <Input
          id="otp-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          required
          autoFocus
          autoComplete="one-time-code"
          placeholder="••••••"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="mt-1.5 tracking-[0.5em] text-center text-lg font-bold"
          data-testid="login-otp-code-input"
        />
        <p className="text-xs text-foreground/60 mt-2">
          Sent to <span className="font-semibold">{email}</span>. Expires in 10 minutes.
        </p>
      </div>
      {error && <div className="text-sm text-destructive" data-testid="login-otp-verify-error">{error}</div>}
      <Button
        type="submit"
        disabled={loading || code.length < 4}
        className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold"
        data-testid="login-otp-verify-button"
      >
        {loading ? "Verifying…" : "Verify & sign in"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={() => { setStep("email"); setCode(""); setError(""); }}
          className="text-foreground/60 hover:underline"
          data-testid="login-otp-change-email"
        >
          Change email
        </button>
        <button
          type="button"
          disabled={resendIn > 0 || loading}
          onClick={() => send()}
          className="brand-orange font-semibold hover:underline disabled:text-foreground/40 disabled:no-underline"
          data-testid="login-otp-resend-button"
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}

export default function Login() {
  const nav = useNavigate();
  const [tab, setTab] = useState("password");

  const onSuccess = (u) => nav(u.role === "super_admin" ? "/admin" : "/dashboard");

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex bg-brand-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, #FF6500 0%, transparent 40%), radial-gradient(circle at 80% 80%, #FF6500 0%, transparent 35%)"
        }} />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Logo size={48} variant="dark" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">Welcome back</div>
            <h1 className="font-heading font-bold text-4xl xl:text-5xl leading-tight">
              Let&rsquo;s serve up something delicious today.
            </h1>
            <p className="text-white/60 mt-4 max-w-md">
              Manage your digital menu, update specials in real-time, and keep your diners hooked.
            </p>
          </div>
          <div className="text-xs text-white/40">© 2026 MenuMojo</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h2 className="font-heading font-bold text-3xl">Sign in</h2>
          <p className="text-foreground/60 mt-2 text-sm">Access your restaurant dashboard.</p>

          <div className="mt-6 inline-flex rounded-full border border-border bg-muted/40 p-1 w-full" role="tablist" data-testid="login-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 px-4 h-9 text-sm rounded-full font-semibold transition-colors ${
                  tab === t.id ? "bg-white shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"
                }`}
                data-testid={`login-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "password" ? <PasswordTab onSuccess={onSuccess} /> : <OtpTab onSuccess={onSuccess} />}

          <div className="mt-6 text-sm text-foreground/60">
            No account?{" "}
            <Link to="/signup" className="brand-orange font-semibold hover:underline" data-testid="signup-link">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
